import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import { useQuery } from "wasp/client/operations";
import {
  getPlatformSectionVisibility,
  updatePlatformSectionVisibility,
} from "wasp/client/operations";
import DefaultLayout from "../../admin/layout/DefaultLayout";
import LoadingSpinner from "../../admin/layout/LoadingSpinner";
import { Switch } from "../../components/ui/switch";
import { EnhancedActionButton } from "../../components/ui/enhanced-page-layout";
import { Save, Settings2 } from "lucide-react";
import {
  SECTION_LABELS,
  SIDEBAR_SECTION_KEYS,
} from "../location-types/constants";
import { SECTIONS_WITH_OPTIONAL_FIELDS } from "./optionalFieldConstants";
import { toast } from "../../hooks/use-toast";

const SECTION_GROUPS: { label: string; keys: readonly string[] }[] = [
  { label: "Information", keys: ["showNotice", "showEvent"] },
  {
    label: "Directory",
    keys: [
      "showTenantBusiness",
      "showRetail",
      "showFoodCourt",
      "showFacility",
      "showService",
      "showBuySell",
      "showRequestOffer",
      "showJob",
    ],
  },
  {
    label: "Community",
    keys: [
      "showUrgentHelp",
      "showAskForHelp",
      "showShareWithNeighbors",
      "showLostFound",
      "showOtherRequests",
    ],
  },
  {
    // Shared across all portal packs (education, skyscraper, retail, …)
    label: "Building management",
    keys: [
      "showBuildingDocument",
      "showWorkOrder",
      "showBuildingSchedule",
      "showBuildingSafety",
      "showUpkeepRequest",
    ],
  },
];

/** Sort section keys alphabetically by their display label */
function sortKeysByLabel(keys: readonly string[]): string[] {
  return [...keys].sort((a, b) => {
    const labelA = SECTION_LABELS[a] ?? a;
    const labelB = SECTION_LABELS[b] ?? b;
    return labelA.localeCompare(labelB);
  });
}

export default function AdminPlatformSectionVisibilityPage() {
  const { data: user } = useAuth();
  const { data, isLoading, error, refetch } = useQuery(
    getPlatformSectionVisibility,
  );
  const [localByKey, setLocalByKey] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  const byKey =
    (data as { byKey?: Record<string, boolean> } | undefined)?.byKey ?? {};

  useEffect(() => {
    const initial: Record<string, boolean> = {};
    for (const key of SIDEBAR_SECTION_KEYS) {
      initial[key] = byKey[key] !== false;
    }
    setLocalByKey(initial);
  }, [byKey]);

  const handleToggle = (sectionKey: string, visible: boolean) => {
    setLocalByKey((prev) => ({ ...prev, [sectionKey]: visible }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePlatformSectionVisibility({ updates: localByKey });
      await refetch();
      toast({
        title: "Saved",
        description: "Global section visibility updated.",
      });
    } catch (e: any) {
      toast({
        title: "Error",
        description: e.message || "Failed to update visibility",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <DefaultLayout user={null as any}>
        <div className="p-4 m-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="text-yellow-800 font-semibold">
            Authentication Required
          </div>
          <div className="text-yellow-600 mt-1">
            Please log in to access this page.
          </div>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout user={user as any}>
      <div className="w-full">
        {/* Header - match Location Types: same padding, border, title + button on same line */}
        <div className="border-b border-border min-h-[4.5rem] flex flex-col justify-center bg-background">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 max-w-[70%]">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white break-words">
                  Platform section visibility
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 break-words">
                  Hide sections globally for the entire platform. When a section
                  is hidden here, it is hidden everywhere and will not be listed
                  in Location Types or in per-location overrides. For sections
                  that support it, use &quot;Fields&quot; to show or hide
                  optional fields (e.g. reward in Lost &amp; Found).
                </p>
              </div>
              <div className="flex items-center gap-3">
                <EnhancedActionButton
                  onClick={handleSave}
                  icon={Save}
                  label={saving ? "Saving..." : "Save changes"}
                  disabled={saving}
                  hideLabelOnMobile
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content - match Location Types content padding */}
        <div className="px-4 py-6 md:px-6">
          {isLoading && <LoadingSpinner />}
          {error && (
            <div className="p-4 mb-6 bg-red-50 border border-red-200 rounded-md text-red-800 dark:bg-red-950/30 dark:border-red-800 dark:text-red-200">
              {error.message || "Failed to load visibility settings"}
            </div>
          )}

          {!isLoading && !error && (
            <div className="space-y-8">
              {SECTION_GROUPS.map((group) => (
                <div key={group.label}>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    {group.label}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    Toggle visibility for sections in the &quot;{group.label}
                    &quot; area of the sidebar.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {sortKeysByLabel(group.keys).map((key) => {
                      const hasFields =
                        SECTIONS_WITH_OPTIONAL_FIELDS.includes(key);
                      return (
                        <div
                          key={key}
                          className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-gray-50/50 hover:bg-gray-50 dark:bg-gray-800/50 dark:hover:bg-gray-800"
                        >
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {SECTION_LABELS[key] ?? key}
                          </span>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {hasFields && (
                              <Link
                                to={`/admin/section-visibility/fields/${key}`}
                                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                              >
                                <Settings2 className="w-3.5 h-3.5" />
                                Fields
                              </Link>
                            )}
                            <Switch
                              checked={localByKey[key] !== false}
                              onCheckedChange={(checked) =>
                                handleToggle(key, checked)
                              }
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DefaultLayout>
  );
}
